import { useNavigate, useParams } from "react-router-dom";
import { Activity, AlertCircle, AlertTriangle, ArrowLeft, CheckCircle, Cloud, ExternalLink, Printer } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useIncidentDetail } from "../hooks";
import { useAuth } from "../hooks/useAuth";
import { formatDateTime } from "../utils";
import { changelogChanges } from "../components/Status/statusFormat";
import { ErrorBanner, LoadingText, Select } from "../components/ui";
import { IncidentBadge, SeverityBadge } from "../components/Status/badges";
import type { HealthEvent } from "../types/health";

const ACTION_LABEL: Record<string, string> = {
  "incident.opened": "Incident opened",
  "incident.resolved": "Marked resolved",
  "incident.auto_resolved": "Auto-resolved",
  "incident.overridden": "Overridden",
  "incident.signals_attached": "Signals attached",
  "incident.severity_escalated": "Severity escalated",
  "incident.postmortem_updated": "Post-mortem updated",
};

const CATEGORY_LABEL: Record<string, string> = {
  error: "APP ERRORS",
  alarm: "INFRA",
  availability: "UPTIME",
};

const DETECTED_LABEL: Record<string, string> = {
  url: "MONITORED URL",
  integration: "INTEGRATION",
  alarm: "CLOUDWATCH",
};

function formatDuration(seconds: number | null): string {
  if (seconds == null) return "ongoing";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function platformLabel(platform: string): string {
  return platform === "shared" ? "All" : platform.charAt(0).toUpperCase() + platform.slice(1);
}

function incidentNumber(id: string): string {
  return `INC-${id.slice(0, 8).toUpperCase()}`;
}

const SOURCE_ICON: Record<string, { Icon: LucideIcon; cls: string }> = {
  cloudwatch: { Icon: Cloud, cls: "border-blue-200 bg-blue-50 text-blue-500" },
  signoz: { Icon: AlertTriangle, cls: "border-orange-200 bg-orange-50 text-orange-500" },
  uptimerobot: { Icon: Activity, cls: "border-blue-200 bg-blue-50 text-blue-500" },
};

type TimelineItem = {
  key: string;
  time: string;
  label: string;
  title: string;
  description?: string | null;
  severity?: string | null;
  href?: string | null;
  Icon: LucideIcon;
  iconCls: string;
};

function sourceLink(details: Record<string, unknown> | null): string | null {
  if (!details) return null;
  const candidate = details.url ?? details.link ?? details.permalink ?? details.console_url;
  return typeof candidate === "string" && /^https?:\/\//.test(candidate) ? candidate : null;
}

function esc(value: unknown): string {
  return String(value ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] ?? c));
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 py-2.5 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{children}</span>
    </div>
  );
}

export default function IncidentDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role?.name ?? "");

  const { data, loading, error, override } = useIncidentDetail(id);

  const timeline: TimelineItem[] = [];
  if (data) {
    timeline.push({
      key: "detected",
      time: formatDateTime(data.started_at),
      label: `${DETECTED_LABEL[data.service_type] ?? "SIGNAL"} · DETECTED`,
      title: data.service_name,
      description: data.service_type === "url" && data.monitored_url
        ? `GET ${data.monitored_url} — ${data.error_message ?? "check failed"}`
        : data.error_message
          ?? (data.service_type === "url" ? "No failure detail was recorded for this check." : null),
      Icon: data.service_type === "alarm" ? Cloud : AlertCircle,
      iconCls: data.service_type === "alarm"
        ? "border-blue-200 bg-blue-50 text-blue-500"
        : "border-amber-200 bg-amber-50 text-amber-500",
    });
    data.timeline.forEach((event: HealthEvent) => {
      const src = SOURCE_ICON[event.source] ?? { Icon: AlertCircle, cls: "border-gray-200 bg-gray-50 text-gray-400" };
      timeline.push({
        key: event.id,
        time: formatDateTime(event.occurred_at),
        label: `${event.source.toUpperCase()} · ${CATEGORY_LABEL[event.category] ?? event.category.toUpperCase()}`,
        title: event.component ?? "Signal",
        description: event.message,
        severity: event.severity,
        href: sourceLink(event.details),
        Icon: src.Icon,
        iconCls: src.cls,
      });
    });
    if (data.resolved_at) {
      timeline.push({
        key: "resolved",
        time: formatDateTime(data.resolved_at),
        label: "ALL SIGNALS · RESOLVED",
        title: "Back to normal",
        description: "The incident was marked resolved.",
        Icon: CheckCircle,
        iconCls: "border-green-200 bg-green-50 text-green-600",
      });
    }
  }

  const sourceCount = data
    ? new Set([data.service_type, ...data.timeline.map((event) => event.source)]).size
    : 0;

  function exportPdf() {
    if (!data) return;
    const win = window.open("", "_blank", "width=820,height=1000");
    if (!win) return;
    const rows = timeline
      .map((item) => `<li><span class="t">${esc(item.time)} · ${esc(item.label)}</span><br/><b>${esc(item.title)}</b>${item.description ? `<br/>${esc(item.description)}` : ""}</li>`)
      .join("");
    const impact = data.computed_impact
      ? `<p><b>Estimated impact:</b> ~${data.computed_impact.registrations_lost} registrations lost (${data.computed_impact.registrations_actual} vs ${data.computed_impact.registrations_expected} expected)</p>`
      : "";
    win.document.write(`<!doctype html><html><head><title>${esc(incidentNumber(data.id))} — ${esc(data.service_name)}</title>
      <style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#111;margin:40px;max-width:720px}
      h1{font-size:20px;margin:0 0 4px} .meta{color:#555;font-size:13px;margin-bottom:16px}
      ul{list-style:none;padding:0} li{border-left:2px solid #ddd;padding:0 0 14px 14px;margin-left:6px}
      .t{color:#888;font-size:11px;text-transform:uppercase;letter-spacing:.05em} h2{font-size:14px;margin:20px 0 8px}</style></head>
      <body><h1>${esc(data.pm_title ?? data.service_name)}</h1>
      <p class="meta">${esc(incidentNumber(data.id))} · ${esc(data.severity)} · ${esc(platformLabel(data.platform))} · ${esc(formatDateTime(data.started_at))} → ${data.resolved_at ? esc(formatDateTime(data.resolved_at)) : "ongoing"} · ${esc(formatDuration(data.duration_seconds))}</p>
      ${data.pm_description ? `<p>${esc(data.pm_description)}</p>` : ""}
      ${data.technical_summary ? `<p><b>Technical:</b> ${esc(data.technical_summary)}</p>` : ""}
      ${impact}
      <h2>What happened, in order</h2><ul>${rows}</ul></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex items-center gap-1.5 text-sm">
        <button
          onClick={() => navigate("/status?tab=incidents")}
          className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Incidents
        </button>
        {data && <span className="text-gray-400">› {incidentNumber(data.id)}</span>}
      </div>

      {loading && !data ? (
        <LoadingText>Loading incident…</LoadingText>
      ) : error ? (
        <ErrorBanner>{error}</ErrorBanner>
      ) : !data ? null : (
        <>
          <h1 className="text-xl font-semibold text-gray-900">{data.service_name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <SeverityBadge severity={data.severity} />
            <IncidentBadge status={data.status} />
            <span>{platformLabel(data.platform)}</span>
          </div>

          <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_320px]">
            <div className="space-y-5">
              {(data.pm_description || data.technical_summary) && (
                <section className="rounded-xl border border-gray-200 bg-white p-5">
                  <h2 className="mb-3 text-base font-semibold text-gray-900">{data.pm_title ?? "What this meant"}</h2>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">In plain language</p>
                      <p className="text-sm text-gray-700">{data.pm_description ?? "—"}</p>
                    </div>
                    <div className="sm:border-l sm:border-gray-100 sm:pl-5">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Technical detail</p>
                      <p className="text-sm text-gray-700">{data.technical_summary ?? "—"}</p>
                    </div>
                  </div>
                </section>
              )}

              <section className="rounded-xl border border-gray-200 bg-white p-5">
                <h2 className="mb-4 text-base font-semibold text-gray-900">What happened, in order</h2>
                <ol className="space-y-6">
                  {timeline.map((item, index) => (
                    <li key={item.key} className="relative flex gap-4">
                      {index < timeline.length - 1 && (
                        <span className="absolute left-4 top-9 h-[calc(100%-4px)] w-px bg-gray-200" aria-hidden="true" />
                      )}
                      <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${item.iconCls}`}>
                        <item.Icon size={14} aria-hidden="true" />
                      </span>
                      <div className="min-w-0 pt-1">
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-400">
                          {item.time} · {item.label}
                        </p>
                        <p className="mt-0.5 text-sm font-semibold text-gray-900">{item.title}</p>
                        {item.description && (
                          <p className="mt-0.5 text-sm text-gray-500">{item.description}</p>
                        )}
                        {(item.severity || item.href) && (
                          <div className="mt-1.5 flex items-center gap-2">
                            {item.severity && <SeverityBadge severity={item.severity} />}
                            {item.href && (
                              <a
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-0.5 text-xs text-blue-600 hover:underline"
                              >
                                View source
                                <ExternalLink size={10} aria-hidden="true" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            </div>

            <aside className="space-y-5">
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Summary</h2>
                <SummaryRow label="Platform">{platformLabel(data.platform)}</SummaryRow>
                <SummaryRow label="Detected">{formatDateTime(data.started_at)}</SummaryRow>
                <SummaryRow label="Resolved">
                  {data.resolved_at ? formatDateTime(data.resolved_at) : "—"}
                </SummaryRow>
                <SummaryRow label="Duration">{formatDuration(data.duration_seconds)}</SummaryRow>
                <SummaryRow label="Severity">
                  <span className="capitalize">{data.severity}</span>
                </SummaryRow>
                {data.timeline.length > 1 && (
                  <SummaryRow label="Grouped from">{data.timeline.length} alarms</SummaryRow>
                )}
                <SummaryRow label="Sources">{sourceCount} signal{sourceCount === 1 ? "" : "s"}</SummaryRow>
                {data.computed_impact && (
                  <SummaryRow label="Impact">
                    <span className="text-red-600">~{data.computed_impact.registrations_lost} registrations</span>
                  </SummaryRow>
                )}

                {data.monitored_url && (
                  <div className="mt-3 border-t border-gray-100 pt-3">
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Endpoint</p>
                    <a
                      href={data.monitored_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 block truncate text-sm text-blue-600 hover:underline"
                      title={data.monitored_url}
                    >
                      {data.monitored_url}
                    </a>
                  </div>
                )}

                <button
                  onClick={() => navigate(`/status/incidents/${data.id}/post-mortem`)}
                  className="mt-4 w-full rounded-lg bg-[#1D345F] py-2 text-sm font-medium text-white hover:bg-[#162847]"
                >
                  View post-mortem
                </button>
                <button
                  onClick={exportPdf}
                  className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 hover:bg-gray-50"
                >
                  <Printer size={14} aria-hidden="true" />
                  Export PDF
                </button>
              </div>

              {isAdmin && (
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Override</h2>
                  <div className="space-y-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">Severity</label>
                      <Select value={override.form.severity} onChange={(e) => override.setField("severity", e.target.value)} className="w-full">
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </Select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">Status</label>
                      <Select value={override.form.status} onChange={(e) => override.setField("status", e.target.value)} className="w-full">
                        <option value="ongoing">Ongoing</option>
                        <option value="resolved">Resolved</option>
                      </Select>
                    </div>
                    {override.form.status === "resolved" && (
                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-500">Resolved at</label>
                        <input
                          type="datetime-local"
                          value={override.form.resolvedAt}
                          onChange={(e) => override.setField("resolvedAt", e.target.value)}
                          className="w-full rounded-lg border border-gray-200 px-2.5 py-2 text-sm text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                        />
                      </div>
                    )}
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">Reason</label>
                      <textarea
                        rows={2}
                        value={override.reasoning}
                        onChange={(e) => override.setReasoning(e.target.value)}
                        placeholder="Why are you changing this?"
                        className="w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
                      />
                    </div>
                    <button
                      onClick={override.save}
                      disabled={override.saving || !override.reasoning.trim()}
                      className="w-full rounded-lg bg-[#1D345F] py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50"
                    >
                      {override.saving ? "Saving…" : override.saved ? "Saved!" : "Save override"}
                    </button>
                  </div>
                </div>
              )}

              {data.changelog && data.changelog.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">Changelog</h2>
                  <ul className="space-y-3">
                    {data.changelog.map((entry, index) => (
                      <li key={index} className="text-sm">
                        <p className="font-medium text-gray-800">{ACTION_LABEL[entry.action] ?? entry.action}</p>
                        <p className="text-xs text-gray-400">{formatDateTime(entry.created_at)} · {entry.actor}</p>
                        {changelogChanges(entry.meta).map((line, j) => (
                          <p key={j} className="mt-0.5 text-xs text-gray-500">{line}</p>
                        ))}
                        {(entry.meta as { reasoning?: string } | null)?.reasoning && (
                          <p className="mt-0.5 text-xs italic text-gray-400">
                            "{(entry.meta as { reasoning?: string }).reasoning}"
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
